"use client";

import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import axiosInstance from '@/api/axios';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const COLORS = {
  recycled: '#10b981', // green
  savings: '#3b82f6'   // blue
};

const CO2SavingsCard = ({
  id = 'R6',
  wasteitem = 'organic',
  dayvalue = 'year',
  datevalue = [{ startDate: null, endDate: null, key: 'selection' }],
  selectedButton_ = 'year'
}) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  // Fallback translations for backward compatibility
  const translations = {
    chartTitle: t('co2SavingsChart.chartTitle') || "CO₂ Savings",
    recycledLabel: t('co2SavingsChart.recycledLabel') || "CO₂ Production",
    savingsLabel: t('co2SavingsChart.savingsLabel') || "CO₂ Savings",
    unsortedLabel: t('co2SavingsChart.unsortedLabel') || "Unsorted CO₂ Production",
    loading: t('co2SavingsChart.loading') || "Loading data...",
    loadError: t('co2SavingsChart.loadError') || "Failed to load data. Please try again later.",
    noData: t('co2SavingsChart.noData') || "No data available for selected period"
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get("/co2savings", {
        params: {
          id,
          wasteitem,
          day: dayvalue,
          withcalendar: selectedButton_ === 'custom'
        },
        timeout: 10000
      });

      if (!response.data) {
        throw new Error(translations.loadError);
      }

      let transformedData = [];

      if (response.data.savings && response.data.recycled) {
        transformedData = Object.keys(response.data.savings).map(key => ({
          name: key,
          recycled: response.data.recycled[key] || 0,
          savings: response.data.savings[key] || 0
        }));
      } else if (Array.isArray(response.data)) {
        transformedData = response.data.map(item => ({
          name: item.date || item.name,
          recycled: item.recycled || 0,
          savings: item.savings || 0
        }));
      }

      setChartData(transformedData);
    } catch (err) {
      console.error("API Error:", err);
      if (wasteitem !== 'pmd') {
        setError(err.message || translations.loadError);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    if (id && wasteitem && dayvalue) {
      fetchData();
    }

    return () => {
      controller.abort();
    };
  }, [id, wasteitem, dayvalue, selectedButton_]);

  // Calculate totals for the summary badges
  const totals = chartData.reduce((acc, item) => ({
    recycled: acc.recycled + item.recycled,
    savings: acc.savings + item.savings
  }), { recycled: 0, savings: 0 });

  const efficiencyRatio = totals.recycled > 0
    ? (totals.savings / totals.recycled).toFixed(1)
    : 0;
  const isEfficient = efficiencyRatio >= 1.0;

  if (loading) {
    return (
      <div className="sm:rounded-lg bg-muted/50 p-2">
        <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">
          {translations.chartTitle}
        </h2>
        <div className="aspect-[4/3] md:aspect-[16/9] flex flex-col items-center justify-center">
          <p>{translations.loading}</p>
        </div>
      </div>
    );
  }

  if (error && wasteitem !== 'pmd') {
    return (
      <div className="sm:rounded-lg bg-muted/50 p-2">
        <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">
          {translations.chartTitle}
        </h2>
        <div className="aspect-[4/3] md:aspect-[16/9] flex flex-col items-center justify-center p-4">
          <p className="text-red-500 mb-4 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sm:rounded-lg bg-muted/50 p-2">
      <div className="flex items-center justify-between">
        <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">
          {translations.chartTitle}
        </h2>
      </div>

      <div className="aspect-[4/3] md:aspect-[16/9]">
        <div className="flex flex-col h-full w-full gap-0">
          {/* Main Chart Area */}
          <div className="flex-1 min-h-0">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))"/>
                  <XAxis
                    dataKey="name"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10 }}
                    width={30}
                  />
                  <Area
                    type="natural"
                    dataKey="recycled"
                    stroke={COLORS.recycled}
                    fill={COLORS.recycled}
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                  <Area
                    type="natural"
                    dataKey="savings"
                    stroke={COLORS.savings}
                    fill={COLORS.savings}
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">{translations.noData}</p>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex justify-center mt-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: COLORS.recycled }}
                />
                <span className="text-xs font-medium">{translations.recycledLabel}</span>
              </div>
              <div className="flex items-center gap-1">
                <div
                  className="w-3 h-0.5"
                  style={{ backgroundColor: COLORS.savings }}
                />
                <span className="text-xs font-medium">{translations.unsortedLabel}</span>
              </div>
              <div className="flex items-center gap-1">
                <div
                  className="w-3 h-3 rounded-full opacity-20"
                  style={{ backgroundColor: COLORS.savings }}
                />
                <span className="text-xs font-medium">{translations.savingsLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CO2SavingsCard;